import { create } from 'zustand';

import { COMPANY_INITIAL_SORT_STATUS } from '@/configs/filter';
import service from '@/services/index';
import { INITIAL_FILTER_SET_QUERY } from '@/types/dataTableConst';
import { ACCOUNT_COLUMNS } from '@/types/filterKeyConst';
import filterUtils from '@/utils/filter';
import { deleteObjectField } from '@/utils/object';
import { getCursorInfoV2, getPaginationQueries } from '@/utils/pagination';
import { arrayToMap, buildQuery, pushEntryIntoQueryArr } from '@/utils/util';

import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '../../../stores/pure/DetailModalStore';

type States = {
  companyMap: CompanyMapT;
  cursors: PaginationCursorsTV2;
  selectedCompany: string | null;
  createModalStatus: boolean;
  page: number;
  listLoading: boolean;
};

type Actions = {
  getCompany: (alias: string) => Promise<CompanyT | null>;
  setState: <T extends keyof States>(state: Record<T, States[T]>) => void;
  setSelectedCompany: (key: string | null) => void;
  setCreateModalStatus: (status: boolean) => void;
  addCompany: (params: PostCompanyParams) => Promise<CompanyT>;
  setNewCompany: (data: CompanyT) => void;
  removeCompany: (key: string) => void;
  fetchCompanyList: (options?: {
    prevData?: PaginatedData<any>[];
    page?: number;
    searchValue?: string;
    queries?: QueryT[];
    sortStatus?: NotNullableSortStatusT;
    fiterSetQuery?: FilterSetQuery;
  }) => Promise<PaginatedData<CompanyT>[]>;
  fetchCompany: (companyAlias?: string) => Promise<CompanyT | null>;
  initCompanyStore: () => void;

  changeCompanyAttr: <K extends keyof CompanyT>(
    alias: string,
    target: K,
    value: CompanyT[K] | null,
  ) => Promise<boolean>;
  setCompanyAttr: <K extends keyof CompanyT>(
    alias: string,
    target: K,
    value: CompanyT[K] | null,
  ) => void;
};

const initialState: States = {
  companyMap: {},
  cursors: {
    first: null,
    last: null,
  },
  selectedCompany: null,
  createModalStatus: false,
  page: 0,
  listLoading: false,
};

export const COMPANY_PAGE_SIZE = 20;

const useCompanyStore = create<States & Actions>((set, get) => ({
  ...initialState,
  getCompany: async (alias) => {
    if (get().companyMap[alias]) {
      return get().companyMap[alias];
    }
    return await get().fetchCompany(alias);
  },
  setState(state) {
    set(state);
  },
  setSelectedCompany: (key) => {
    const { setNewModal } = useDetailModalStore.getState();
    set((state) => ({
      ...state,
      selectedCompany: key,
    }));

    if (key)
      setNewModal({
        alias: key,
        type: DETAIL_MODAL_TYPE.COMPANY,
        idx: 0,
      });
  },
  setCreateModalStatus: (status) => {
    set((state) => ({
      ...state,
      createModalStatus: status,
    }));
  },
  addCompany: async (company) => {
    const data = await service.account.postCompany(company);
    return data;
  },
  setNewCompany: (data) => {
    const { page } = get();
    if (page !== 0) return;

    const company = { ...data, feedback_count: 0 };

    set((state) => {
      let prevArr = Object.values(state.companyMap);
      if (!state.companyMap[company.alias]) {
        if (prevArr.length >= COMPANY_PAGE_SIZE) {
          prevArr = prevArr.slice(0, -1);
        }
      }
      const newArr = [company, ...prevArr];
      return {
        companyMap: newArr.reduce(
          (acc, value) => {
            acc[value.alias] = value;
            return acc;
          },
          {} as Record<string, CompanyT>,
        ),
      };
    });
  },
  removeCompany: async (key) => {
    await service.account.deleteCompany(key);
    const newCompanyMap = deleteObjectField(get().companyMap, key);
    set({ companyMap: newCompanyMap });
  },
  fetchCompanyList: async (options) => {
    const prevData = options?.prevData || [];
    const page = options?.page || undefined;
    const searchValue = options?.searchValue || undefined;
    const queries = options?.queries || [];
    const sortStatus = options?.sortStatus || COMPANY_INITIAL_SORT_STATUS;
    const fiterSetQuery = options?.fiterSetQuery || INITIAL_FILTER_SET_QUERY;

    const order = sortStatus
      ? sortStatus.orderStatus === 'unset'
        ? 'asc'
        : sortStatus.orderStatus
      : 'asc';

    const { page: currPage } = get();

    const filterQueryArr = filterUtils.getFilterStatusQuery(
      fiterSetQuery || INITIAL_FILTER_SET_QUERY,
    );

    const paginationQueryArr = getPaginationQueries({
      queries: {},
      prevData: prevData || [],
      targetPage: page,
      currPage,
      sortby: sortStatus?.sortBy as keyof CompanyT,
      order,
      cursorKey: 'paginate_token',
      acc: !!page,
    });

    const queryArr = pushEntryIntoQueryArr(
      [...(queries || []), ...filterQueryArr, ...paginationQueryArr],
      {
        [ACCOUNT_COLUMNS.NAME_SEARCH]: searchValue || null,
      },
    );

    const query = buildQuery(queryArr);

    set({ listLoading: true });

    const result = await service.account.fetchCompanyList(query);

    const companies = arrayToMap(result, 'alias');

    const newCursors = getCursorInfoV2(
      result,
      sortStatus?.sortBy as keyof CompanyT,
    );

    set({
      companyMap: companies,
      cursors: newCursors,
      page: page || 0,
      listLoading: false,
    });

    return result;
  },

  async fetchCompany(companyAlias) {
    if (!companyAlias) return null;
    const res = await service.account.fetchCompany(companyAlias);
    set((prev) => ({
      ...prev,
      companyMap: {
        ...prev.companyMap,
        [companyAlias]: res,
      },
    }));
    return res;
  },

  async changeCompanyAttr(alias, target, value) {
    if (value === null) {
      const data = await service.account.resetCompanyAttr(alias, target);
      if (data) {
        get().setCompanyAttr(alias, target, value);
      }
      return data;
    } else {
      const data = await service.account.updateCompanyAttr(
        alias,
        target,
        value,
      );
      if (data) {
        get().setCompanyAttr(alias, target, value);
      }
      return data;
    }
  },

  setCompanyAttr(alias, target, value) {
    let newMap = { ...get().companyMap };
    if (!newMap[alias]) return;
    let newCompany = {
      ...newMap[alias],
    };
    if (value === null) {
      newCompany = deleteObjectField(newCompany, target);
    } else {
      newCompany[target] = value;
    }
    newMap[alias] = newCompany;
    set({ companyMap: newMap });
  },

  initCompanyStore: () => {
    set(initialState);
  },
}));

export default useCompanyStore;
